/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/hooks/usePwaInstall.ts
 *
 * Rôle :
 *   Détecte la possibilité d'INSTALLER la PWA et expose une action
 *   d'installation, multi-plateforme (demande Mika 2026-05-18 : « aucun
 *   message d'installation sur Android ; sur Apple un message qui
 *   propose d'installer + comment ; sur PC/Mac une installation »).
 *
 *   Statuts retournés :
 *     - 'installed'   : déjà installée (display-mode standalone /
 *                       navigator.standalone iOS) → ne rien proposer.
 *     - 'installable' : Android / Chrome / Edge (mobile & desktop)
 *                       l'événement `beforeinstallprompt` a été capté →
 *                       on peut déclencher l'invite native via
 *                       `promptInstall()`.
 *     - 'ios'         : iOS / iPadOS Safari hors PWA → pas d'invite
 *                       native possible → on propose la marche à suivre
 *                       (« Partager » → « Sur l'écran d'accueil »,
 *                       page /install-help-ios déjà existante).
 *     - 'unsupported' : aucun moyen d'installer (navigateur sans
 *                       support, ou bannière refusée par l'utilisateur).
 *
 *   `beforeinstallprompt` peut se déclencher AVANT le montage React :
 *   on le capte au niveau MODULE (listener posé à l'import) + petit
 *   store d'abonnement, pour ne jamais le rater.
 *
 *   Refus mémorisé (localStorage) : on ne harcèle pas l'utilisateur.
 *
 * Réutilisable : 100 %. Aucun couplage TNE.
 *
 * Voir : vite.config.ts (manifest PWA), InstallHelpIos.tsx,
 *        CLAUDE.md §8.2 (PWA cross-plateforme), §5 2026-05-18.
 *
 * @author Mika
 * @since  2026-05-18
 */

import { useSyncExternalStore, useCallback } from 'react';

/** Événement non standard `beforeinstallprompt` (Chromium). */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'tne.pwa.install.dismissed';

// ── Store module-level (capté avant le montage React) ────────────────────
let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // 2026-10-05 : un navigateur sans matchMedia (ancien, ou environnement de
  // test) ne doit pas faire tomber l'écran qui appelle ce hook.
  const mm =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  // iOS Safari : navigator.standalone (non standard mais réel).
  const iosStandalone =
    // @ts-expect-error 2026-05-18, navigator.standalone non typé (iOS).
    window.navigator.standalone === true;
  return mm || iosStandalone;
}

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIDevice = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ se présente en « Macintosh » mais a un écran tactile.
  const isIpadOs =
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1;
  // Exclure les navigateurs in-app qui ne supportent pas l'add-to-home.
  const isWebkit = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
  return (isIDevice || isIpadOs) && isWebkit;
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    // Empêche la mini-infobar Chrome → on pilote nous-mêmes l'UI.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installed = true;
    try {
      window.localStorage.removeItem(DISMISS_KEY);
    } catch {
      /* stockage indisponible, sans effet */
    }
    emit();
  });
}

export type PwaInstallStatus =
  | 'installed'
  | 'installable'
  | 'ios'
  | 'unsupported';

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Snapshot stable (référence dépend uniquement de `deferredPrompt`/`installed`). */
function getSnapshot(): BeforeInstallPromptEvent | null {
  return deferredPrompt;
}
function getInstalledSnapshot(): boolean {
  return installed;
}

export interface UsePwaInstall {
  readonly status: PwaInstallStatus;
  /** Déclenche l'invite native (Android/desktop). No-op sinon. */
  readonly promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
  /** Mémorise le refus (localStorage), n'affiche plus la bannière. */
  readonly dismiss: () => void;
  /** true si l'utilisateur a déjà refusé (persisté). */
  readonly dismissed: boolean;
}

export function usePwaInstall(): UsePwaInstall {
  const prompt = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const isInstalled = useSyncExternalStore(
    subscribe,
    getInstalledSnapshot,
    getInstalledSnapshot,
  );

  let dismissed = false;
  try {
    dismissed =
      typeof window !== 'undefined' &&
      window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    dismissed = false;
  }

  let status: PwaInstallStatus;
  if (isInstalled || isStandalone()) {
    status = 'installed';
  } else if (prompt) {
    status = 'installable';
  } else if (isIosSafari()) {
    status = 'ios';
  } else {
    status = 'unsupported';
  }

  const promptInstall = useCallback(async (): Promise<
    'accepted' | 'dismissed' | 'unavailable'
  > => {
    if (!deferredPrompt) return 'unavailable';
    const ev = deferredPrompt;
    try {
      await ev.prompt();
      const choice = await ev.userChoice;
      // Un beforeinstallprompt n'est utilisable qu'une fois.
      deferredPrompt = null;
      emit();
      return choice.outcome;
    } catch {
      return 'unavailable';
    }
  }, []);

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* stockage indisponible, sans effet */
    }
    emit();
  }, []);

  return { status, promptInstall, dismiss, dismissed };
}
